class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    this.addEventListener('change', (event) => {
      if (
        event.target.closest(
          '.custom-price-filter input[type="checkbox"]'
        )
      ) {
        this.onCustomPriceFilterChange(event);
      }
    });

    this.saveOriginalCustomPriceRanges();
    this.restoreCustomPriceFilterState();

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html.innerHTML);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    const facetForm = document.querySelector('facet-filters-form');

    if (facetForm) {
      facetForm.restoreOriginalCustomPriceRanges();
      facetForm.restoreCustomPriceFilterState();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  saveOriginalCustomPriceRanges() {
    const wrappers = document.querySelectorAll('[data-custom-price-filter-wrapper]');
    if (!wrappers.length) return;
  
    // Only ever capture the pristine, server-rendered state once per page load.
    if (this.originalCustomPriceRanges) return;
  
    this.originalCustomPriceRanges = Array.from(wrappers).map((wrapper) => {
      // Identify this wrapper as belonging to the desktop or mobile filter
      // form so we can restore each one independently later.
      const contextForm = wrapper.closest('form');
      const contextId = contextForm ? contextForm.id : 'unknown';
  
      const options = wrapper.querySelectorAll('[data-custom-price-option]');
      const ranges = Array.from(options).map((input) => ({
        min: input.dataset.min || '0',
        max: input.dataset.max || '',
      }));
  
      return { contextId, ranges };
    });
  }
  
  restoreOriginalCustomPriceRanges() {
    if (!this.originalCustomPriceRanges?.length) return;
  
    const wrappers = document.querySelectorAll('[data-custom-price-filter-wrapper]');
    if (!wrappers.length) return;
  
    wrappers.forEach((wrapper) => {
      const contextForm = wrapper.closest('form');
      const contextId = contextForm ? contextForm.id : 'unknown';
  
      const saved = this.originalCustomPriceRanges.find(
        (entry) => entry.contextId === contextId
      );
      if (!saved) return;
  
      wrapper.innerHTML = '';
  
      const ul = document.createElement('ul');
      ul.className =
        'facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled';
      ul.setAttribute('role', 'list');
  
      saved.ranges.forEach((range) => {
        const min = Number(range.min);
        const max = range.max !== '' ? Number(range.max) : null;
  
        if (Number.isNaN(min)) return;
        if (max !== null && Number.isNaN(max)) return;
  
        const li = document.createElement('li');
        li.className = 'list-menu__item facets__item';
  
        const label = document.createElement('label');
        label.className = 'custom-price-filter__option facets__label facet-checkbox';
  
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset.customPriceOption = '';
        input.dataset.min = String(min);
        if (max !== null) input.dataset.max = String(max);
  
        const span = document.createElement('span');
        if (min === 0 && max !== null) {
          span.textContent = `Under ${this.formatCustomPrice(max)}`;
        } else if (max !== null) {
          span.textContent = `${this.formatCustomPrice(min)}–${this.formatCustomPrice(max)}`;
        } else {
          span.textContent = `${this.formatCustomPrice(min)}+`;
        }
  
        label.appendChild(input);
        label.appendChild(span);
        li.appendChild(label);
        ul.appendChild(li);
      });
  
      wrapper.appendChild(ul);
    });
  }
  formatCustomPrice(amount) {
    const number = Number(amount);

    if (Number.isNaN(number)) {
      return '$0';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(number);
  }

  onCustomPriceFilterChange(event) {
    const priceInput = event.target.closest(
      '.custom-price-filter input[type="checkbox"]'
    );

    if (!priceInput) return;

    const form = priceInput.closest('form');

    if (!form) return;

    /*
    * Only allow ONE custom price range at a time.
    */
    const allPriceInputs = form.querySelectorAll(
      '.custom-price-filter input[type="checkbox"]'
    );

    allPriceInputs.forEach((input) => {
      if (input !== priceInput) {
        input.checked = false;
      }
    });

    /*
    * Remove old custom price hidden inputs.
    */
    form
      .querySelectorAll('[data-custom-price-filter]')
      .forEach((input) => {
        input.remove();
      });

    /*
    * If current price filter is unchecked,
    * remove the Shopify price filter.
    */
    if (!priceInput.checked) {
      const searchParams =
        this.createSearchParams(form);

      this.onSubmitForm(
        searchParams,
        event
      );

      return;
    }

    /*
    * Add minimum price.
    */
    const minInput =
      document.createElement('input');

    minInput.type = 'hidden';
    minInput.name =
      'filter.v.price.gte';

    minInput.value =
      priceInput.dataset.min;

    minInput.dataset.customPriceFilter =
      'true';

    form.appendChild(minInput);

    /*
    * Add maximum price if available.
    */
    if (
      priceInput.dataset.max !== undefined &&
      priceInput.dataset.max !== ''
    ) {
      const maxInput =
        document.createElement('input');

      maxInput.type = 'hidden';
      maxInput.name =
        'filter.v.price.lte';

      maxInput.value =
        priceInput.dataset.max;

      maxInput.dataset.customPriceFilter =
        'true';

      form.appendChild(maxInput);
    }

    /*
    * Submit using Shopify AJAX filtering.
    */
    const searchParams =
    this.createSearchParams(form);

    this.onSubmitForm(
      searchParams,
      event
    );
  }

  restoreCustomPriceFilterState() {
    const params = new URLSearchParams(
      window.location.search
    );

    const gte = params.get('filter.v.price.gte');
    const lte = params.get('filter.v.price.lte');

    const checkboxes = document.querySelectorAll(
      '[data-custom-price-option]'
    );

    if (!checkboxes.length) return;

    /*
     * Reset all price checkboxes.
     */
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    /*
     * No price filter selected.
     */
    if (gte === null && lte === null) {
      return;
    }

    const minPrice =
      gte !== null ? Number(gte) : 0;

    const maxPrice =
      lte !== null ? Number(lte) : null;

    checkboxes.forEach((checkbox) => {
      const checkboxMin =
        Number(checkbox.dataset.min || 0);

      const checkboxMax =
        checkbox.dataset.max !== undefined &&
        checkbox.dataset.max !== ''
          ? Number(checkbox.dataset.max)
          : null;

      const minMatches =
        checkboxMin === minPrice;

      const maxMatches =
        checkboxMax === maxPrice ||
        (
          checkboxMax === null &&
          maxPrice === null
        );

      if (minMatches && maxMatches) {
        checkbox.checked = true;
      }
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeInputs = this.querySelectorAll(
      'input[type="text"], input[type="number"], input[type="range"]'
    );
 
    if (this.rangeInputs.length < 2) return;
 
    this.rangeInputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }
 
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }
 
  onKeyDown(event) {
    if (event.metaKey) return;
 
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }
 
  setMinAndMaxValues() {
    if (!this.rangeInputs || this.rangeInputs.length < 2) return;
 
    const minInput = this.rangeInputs[0];
    const maxInput = this.rangeInputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }
 
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
 
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
 
customElements.define('price-range', PriceRange);


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

document.addEventListener('click', function (event) {
  const removeLink = event.target.closest('.active-facets__button-remove');
  if (!removeLink) return;
 
  const anchor = removeLink.tagName === 'A' ? removeLink : removeLink.closest('a');
  const href = anchor ? anchor.getAttribute('href') : null;
  if (!href) return;
 
  const targetParams = new URLSearchParams(href.split('?')[1] || '');
  const willHaveMin = targetParams.get('filter.v.price.gte');
  const willHaveMax = targetParams.get('filter.v.price.lte');
 
  if (willHaveMin === null && willHaveMax === null) {
    document.querySelectorAll('[data-custom-price-option]:checked').forEach((checkbox) => {
      checkbox.checked = false;
    });
  }
});